import type { KundaliResponse, PlansResponse, ProductRecommendation } from "@ishvaram/api-client";
import { INTENT_OFFER_RULES, type IntentOfferRule } from "@ishvaram/domain-data/intent-offer";
import {
  CHAT_LANDING_PATHS,
  DEDICATED_REPORT_LANDING_INTENTS,
  DOSHA_TEXT_MATCHERS,
  INTENT_TAXONOMY_VERSION,
  REPORT_LANDING_PATHS,
  SESSION_PLAN_IDS,
  TEXT_INTENT_MATCHERS,
  type CustomerIntent,
} from "./customer-intent-taxonomy";

export type { CustomerIntent } from "./customer-intent-taxonomy";

export type IntentConfidence = "high" | "medium" | "low";
export type IntentSource = "plan" | "chat" | "chart" | "seo" | "landing" | "unknown";
export type PaywallVariant = "chat_first" | "report_first";

export type IntentContextInput = {
  sourcePath?: string;
  seoIntent?: string;
  customerProblem?: string;
  chatText?: string;
  chatSignalCount?: number;
  questionText?: string;
  doshas?: KundaliResponse["doshas"];
  products?: ProductRecommendation[];
  planId?: string;
  planType?: string;
  /** Rendered at the in-chat paywall (not an SEO page CTA). The seeker is
   * actively chatting, so the ₹29 session (lowest-friction "keep chatting"
   * entry) stays surfaced — chat → never_bury: quick. Without this, an
   * SEO-dosha-sourced chatter inherits the source page's report_first and the
   * session gets buried (the f1220f40c regression). */
  isChatPaywall?: boolean;
  /** The seeker's ISO-3166-1 alpha-2 country, when known. Drives the diaspora
   * report pricing swap inside `rankPaywallOffers` — see `country` on
   * IntentContext. Omit it and every seeker gets India pricing. */
  country?: string;
};

export type IntentContext = {
  intent_taxonomy_version: string;
  landing_intent: CustomerIntent;
  chat_intent: CustomerIntent;
  question_intent: CustomerIntent;
  chart_intent: CustomerIntent;
  detected_intent: CustomerIntent;
  intent_confidence: IntentConfidence;
  intent_source: IntentSource;
  paywall_variant: PaywallVariant;
  chat_signal_count: number;
  strongest_dosha_id: string;
  strongest_dosha_severity?: number;
  product_affinity: string;
  /**
   * The seeker's ISO-3166-1 alpha-2 country, when known — the ONLY input to the
   * diaspora report-pricing swap (`applyIntlPricingToOffers`, called inside
   * `rankPaywallOffers`).
   *
   * OPTIONAL BY DESIGN, and the optionality is the safety property: a caller
   * that forgets to pass it gets `undefined` → India pricing → byte-identical to
   * the behavior before intl pricing existed. The failure mode of forgetting is
   * "diaspora seeker pays India prices" (what already happens today), never
   * "Indian seeker is shown a ₹999 price". Never make this required, and never
   * default it to anything but the home market.
   */
  country?: string;
};

export type RankedPaywallOffer = {
  planId: string;
  planType: "report" | "session" | "subscription" | "credit_pack" | "resolution";
  planName: string;
  priceInr: number;
  ctaRank: number;
  isPrimaryCta: boolean;
  intentFit: "primary" | "secondary" | "tertiary";
  pages?: number;
  billingCycle?: string;
  savings?: string;
  credits?: number;
  perMessageCost?: string;
  popular?: boolean;
  // Multi-currency display (ADR-0048): when the backend priced this seeker in a
  // foreign currency, these carry the amount (minor units, e.g. cents) + ISO
  // currency it WILL charge — the same values create-order pins, so the card
  // matches the Razorpay modal. Absent for Indian seekers / flag off ⇒ render ₹priceInr.
  checkoutAmountMinor?: number;
  checkoutCurrency?: string;
};

type UnrankedPaywallOffer = Omit<RankedPaywallOffer, "ctaRank" | "isPrimaryCta" | "intentFit">;
type KundaliDosha = KundaliResponse["doshas"][number];

export const GOVERNED_OFFER_INTENT: Record<CustomerIntent, string> = {
  chat_now: "chat",
  low_intent: "chat",
  kundali_report: "birth_chart",
  marriage: "matching",
  career: "career",
  finance: "career",
  remedy_dosha: "dosha_remedy",
  timing: "timing",
  health: "birth_chart",
  unknown: "birth_chart",
};

function normalizePath(path?: string): string {
  if (!path) return "";
  try {
    const parsed = path.startsWith("http") ? new URL(path).pathname : path;
    const cleaned = parsed
      .split("?")[0]
      ?.replace(/\/+/g, "/")
      .replace(/(.+)\/$/, "$1");
    return cleaned || "/";
  } catch {
    return (
      path
        .split("?")[0]
        ?.replace(/\/+/g, "/")
        .replace(/(.+)\/$/, "$1") || ""
    );
  }
}

function includesAny(value: string, terms: string[]): boolean {
  return terms.some((term) => value.includes(term));
}

function intentFromText(value?: string): CustomerIntent {
  const text = (value ?? "").toLowerCase();
  const match = TEXT_INTENT_MATCHERS.find(({ terms }) => includesAny(text, terms));
  return match?.intent ?? "unknown";
}

export function intentFromChatText(value?: string): CustomerIntent {
  const lines = (value ?? "")
    .split("\n")
    .map((line) => line.trim())
    .filter(Boolean)
    .reverse();
  for (const line of lines) {
    const intent = intentFromText(line);
    if (intent !== "unknown" && intent !== "low_intent") return intent;
  }
  return intentFromText(value);
}

export function resolveCustomerIntentContext(input: IntentContextInput): IntentContext {
  const path = normalizePath(input.sourcePath);
  const chatText = input.chatText ?? input.questionText;
  const chat_intent = intentFromChatText(chatText);
  const problemIntent = intentFromText(input.customerProblem);
  const seoIntent = intentFromText(input.seoIntent);
  const planIntent = intentFromPlan(input.planId, input.planType);
  const pathIntent = intentFromPath(path);
  const strongestDosha = topActiveDosha(input.doshas ?? []);
  const chart_intent = strongestDosha ? "remedy_dosha" : "unknown";
  const product_affinity = productAffinity({
    questionText: input.questionText,
    chatText,
    customerProblem: input.customerProblem,
    strongestDosha,
    products: input.products ?? [],
  });
  const landing_intent = pathIntent !== "unknown" ? pathIntent : seoIntent;
  const detected = chooseDetectedIntent({
    landingIntent: landing_intent,
    chatIntent: chat_intent,
    planIntent,
    problemIntent,
    chartIntent: chart_intent,
    seoIntent,
  });

  return {
    intent_taxonomy_version: INTENT_TAXONOMY_VERSION,
    landing_intent,
    chat_intent,
    question_intent: chat_intent,
    chart_intent,
    detected_intent: detected.intent,
    intent_confidence: chooseConfidence({
      landingIntent: landing_intent,
      planIntent,
      chatIntent: chat_intent,
      problemIntent,
      chartIntent: chart_intent,
    }),
    intent_source: detected.source,
    paywall_variant: choosePaywallVariant(
      landing_intent,
      detected.intent,
      planIntent,
      input.isChatPaywall
    ),
    chat_signal_count: input.chatSignalCount ?? countChatSignals(chatText),
    strongest_dosha_id: strongestDosha?.dosha_id ?? "",
    strongest_dosha_severity: strongestDosha?.severity,
    product_affinity,
    country: input.country,
  };
}

function chooseDetectedIntent({
  landingIntent,
  chatIntent,
  planIntent,
  problemIntent,
  chartIntent,
  seoIntent,
}: {
  landingIntent: CustomerIntent;
  chatIntent: CustomerIntent;
  planIntent: CustomerIntent;
  problemIntent: CustomerIntent;
  chartIntent: CustomerIntent;
  seoIntent: CustomerIntent;
}): { intent: CustomerIntent; source: IntentSource } {
  if (planIntent !== "unknown") return { intent: planIntent, source: "plan" };
  if (chatIntent !== "unknown") return { intent: chatIntent, source: "chat" };
  if (problemIntent !== "unknown") return { intent: problemIntent, source: "chat" };
  // An explicit dedicated-report landing (career page → career_report) outranks
  // the incidental chart dosha most kundalis carry (rule 40 never_bury; the
  // 2026-06-09 career_report → ₹0 class). Generic landings (free-kundali,
  // unknown) keep the dosha-first personalisation below — that routing drives
  // the dosha plans' growth and must not regress.
  if (DEDICATED_REPORT_LANDING_INTENTS.has(landingIntent)) {
    return { intent: landingIntent, source: "landing" };
  }
  if (chartIntent !== "unknown" && landingIntent !== "chat_now") {
    return { intent: chartIntent, source: "chart" };
  }
  return detectedFromLandingSignals(seoIntent, landingIntent);
}

function detectedFromLandingSignals(
  seoIntent: CustomerIntent,
  landingIntent: CustomerIntent
): { intent: CustomerIntent; source: IntentSource } {
  if (seoIntent !== "unknown" && !(landingIntent === "chat_now" && seoIntent === "low_intent")) {
    return { intent: seoIntent, source: "seo" };
  }
  if (landingIntent !== "unknown") return { intent: landingIntent, source: "landing" };
  return { intent: "unknown", source: "unknown" };
}

function chooseConfidence(input: {
  landingIntent: CustomerIntent;
  planIntent: CustomerIntent;
  chatIntent: CustomerIntent;
  problemIntent: CustomerIntent;
  chartIntent: CustomerIntent;
}): IntentConfidence {
  if (
    input.planIntent !== "unknown" ||
    input.chatIntent !== "unknown" ||
    input.problemIntent !== "unknown"
  ) {
    return "high";
  }
  if (input.chartIntent !== "unknown" || input.landingIntent !== "unknown") return "medium";
  return "low";
}

function intentFromPath(path: string): CustomerIntent {
  if (CHAT_LANDING_PATHS.has(path)) return "chat_now";
  if (REPORT_LANDING_PATHS.has(path) || path.includes("kundali")) return "kundali_report";
  return intentFromText(path);
}

function intentFromPlan(planId?: string, planType?: string): CustomerIntent {
  if (planType === "session" || (planId && SESSION_PLAN_IDS.has(planId))) return "chat_now";
  if (planType === "report") {
    const planIntent = intentFromText(planId);
    return planIntent === "unknown" ? "kundali_report" : planIntent;
  }
  return "unknown";
}

function choosePaywallVariant(
  landingIntent: CustomerIntent,
  detectedIntent: CustomerIntent,
  planIntent: CustomerIntent,
  isChatPaywall = false
): PaywallVariant {
  // In-chat paywall: the seeker is actively chatting, so the ₹29 session
  // (lowest-friction "keep chatting" entry) stays primary regardless of the
  // SEO source page's intent — chat → never_bury: quick (the f1220f40c fix).
  if (isChatPaywall) return "chat_first";
  if (planIntent !== "unknown" && planIntent !== "chat_now") return "report_first";
  if (landingIntent === "chat_now") return "chat_first";
  return detectedIntent === "chat_now" || detectedIntent === "low_intent"
    ? "chat_first"
    : "report_first";
}

function topActiveDosha(doshas: KundaliDosha[]): KundaliDosha | undefined {
  return [...doshas].filter((dosha) => dosha.is_present).sort((a, b) => b.severity - a.severity)[0];
}

function doshaIdFromText(value?: string): string {
  const text = (value ?? "").toLowerCase();
  return DOSHA_TEXT_MATCHERS.find(({ terms }) => includesAny(text, terms))?.doshaId ?? "";
}

function productAffinity({
  questionText,
  chatText,
  customerProblem,
  strongestDosha,
  products,
}: {
  questionText?: string;
  chatText?: string;
  customerProblem?: string;
  strongestDosha?: KundaliDosha;
  products: ProductRecommendation[];
}): string {
  const productDoshas = new Set(products.map((p) => p.triggered_by_dosha).filter(Boolean));
  const candidates = [
    affinityCandidate(doshaIdFromText(chatText), productDoshas, true),
    affinityCandidate(doshaIdFromText(questionText), productDoshas, true),
    affinityCandidate(doshaIdFromText(customerProblem) || customerProblem, productDoshas),
    affinityCandidate(strongestDosha?.dosha_id, productDoshas),
    products[0]?.triggered_by_dosha,
    strongestDosha?.dosha_id,
  ];
  return candidates.find(Boolean) ?? "";
}

function countChatSignals(chatText?: string): number {
  if (!chatText?.trim()) return 0;
  return chatText
    .split("\n")
    .map((line) => line.trim())
    .filter(Boolean).length;
}

function affinityCandidate(
  doshaId: string | undefined,
  productDoshas: Set<string | null | undefined>,
  allowWithoutProducts = false
): string {
  if (!doshaId) return "";
  if (allowWithoutProducts && productDoshas.size === 0) return doshaId;
  return productDoshas.has(doshaId) ? doshaId : "";
}

function fitForOffer(
  context: IntentContext,
  offer: UnrankedPaywallOffer,
  rule: IntentOfferRule | null,
  policyOrder: string[] | null
): RankedPaywallOffer["intentFit"] {
  if (rule && policyOrder) {
    if (offer.planId === policyOrder[0]) return "primary";
    if (policyOrder.includes(offer.planId)) return "secondary";
    return "tertiary";
  }
  if (context.paywall_variant === "chat_first") {
    return offer.planType === "session" ? "primary" : "secondary";
  }
  return offer.planType === "report" ? "primary" : "secondary";
}

function offerRuleForContext(context: IntentContext): IntentOfferRule | null {
  const intent = governedOfferIntent(context);
  return INTENT_OFFER_RULES.find((rule) => rule.intent === intent) ?? null;
}

function governedOfferIntent(context: IntentContext): string {
  if (context.paywall_variant === "chat_first") return "chat";
  return GOVERNED_OFFER_INTENT[context.detected_intent] ?? "birth_chart";
}

function policyRank(offer: UnrankedPaywallOffer, policyOrder: string[] | null): number {
  if (!policyOrder) return Number.MAX_SAFE_INTEGER;
  const index = policyOrder.indexOf(offer.planId);
  return index === -1 ? Number.MAX_SAFE_INTEGER : index;
}

/**
 * The governed offer order for this render (rule 40): the yaml's
 * [primary, ...secondaries], with the tier-scoped `exhaustedTierOverrides`
 * promotion applied when the paywall payload names an exhausted tier (e.g.
 * session_expired → resolution_7d). The base primary (never_bury) is demoted
 * exactly ONE slot — first in the disclosure, never dropped. An override whose
 * plan is absent from the paywall payload (flag off / control arm strips
 * `resolutions`) is a no-op by construction.
 */
function policyOfferOrder(
  rule: IntentOfferRule | null,
  exhaustedTier: string | null
): string[] | null {
  if (!rule) return null;
  const base = [rule.primaryOffer, ...rule.secondaryOffers];
  const override = exhaustedTier ? rule.exhaustedTierOverrides?.[exhaustedTier] : undefined;
  if (!override || override === rule.primaryOffer) return base;
  return [override, ...base.filter((id) => id !== override)];
}

function paywallExhaustedTier(paywall: PlansResponse): string | null {
  const value = (paywall as { exhausted_tier?: string | null }).exhausted_tier;
  return typeof value === "string" && value ? value : null;
}

export function rankPaywallOffers(
  paywall: PlansResponse,
  context: IntentContext
): RankedPaywallOffer[] {
  const rule = offerRuleForContext(context);
  const policyOrder = policyOfferOrder(rule, paywallExhaustedTier(paywall));
  const offers: UnrankedPaywallOffer[] = [
    // WS-K resolution packages: present ONLY when the client's flag + treatment
    // arm kept them in the payload (visibleResolutionPaywall) — otherwise the
    // list is empty and ranking is byte-identical to today.
    ...(paywall.resolutions ?? []).map((r) => ({
      planId: r.id,
      planType: "resolution" as const,
      planName: r.name,
      priceInr: r.price_inr,
    })),
    ...(paywall.sessions ?? []).map((s) => ({
      planId: s.id,
      planType: "session" as const,
      planName: s.name,
      priceInr: s.price_inr,
    })),
    // ADR-0048 price-book model: report identity never changes by country.
    // `/plans` attaches the buyer-specific presentment amount to the SAME base
    // row, and every ranker consumer carries those fields unchanged.
    ...(paywall.premium_reports ?? []).map((r) => {
      return {
        planId: r.id,
        planType: "report" as const,
        planName: r.name,
        priceInr: r.price_inr,
        pages: r.pages,
        checkoutAmountMinor: r.checkout_amount_minor ?? undefined,
        checkoutCurrency: r.checkout_currency ?? undefined,
      };
    }),
    ...(paywall.subscriptions ?? []).map((s) => ({
      planId: s.id,
      planType: "subscription" as const,
      planName: s.name,
      priceInr: s.price_inr,
      billingCycle: s.billing_cycle,
      savings: s.savings ?? undefined,
    })),
    ...(paywall.credit_packs ?? []).map((p) => ({
      planId: p.id,
      planType: "credit_pack" as const,
      planName: `${p.credits} Credits`,
      priceInr: p.price_inr,
      credits: p.credits,
      perMessageCost: p.per_message_cost,
      popular: p.popular,
    })),
  ];

  return offers
    .map((offer) => ({
      ...offer,
      intentFit: fitForOffer(context, offer, rule, policyOrder),
    }))
    .sort((a, b) => {
      const priority: Record<RankedPaywallOffer["intentFit"], number> = {
        primary: 0,
        secondary: 1,
        tertiary: 2,
      };
      const typeOrder = { resolution: 0, session: 0, report: 1, subscription: 2, credit_pack: 3 };
      const policyDelta = policyRank(a, policyOrder) - policyRank(b, policyOrder);
      return (
        policyDelta ||
        priority[a.intentFit] - priority[b.intentFit] ||
        Number(b.planId === "quick") - Number(a.planId === "quick") ||
        typeOrder[a.planType] - typeOrder[b.planType] ||
        b.priceInr - a.priceInr
      );
    })
    .map((offer, index) => ({
      ...offer,
      ctaRank: index + 1,
      isPrimaryCta: index === 0,
    }));
}
